import { useEffect, useState, useCallback, useRef } from 'react';
import { clientActions } from '../../../actions/client';

// ─── Small field with required/hint/error ────────────────────────────────────
function Field({ label, value, onChange, placeholder, disabled, maxLength, required, hint, error }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>
        {label}{required && <span style={{ color: '#F5576C', marginLeft: 4 }}>*</span>}
      </div>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        style={{
          width: '100%', height: 38, padding: '0 12px', borderRadius: 9,
          background: 'rgba(255,255,255,.05)',
          border: `1px solid ${error ? 'rgba(245,87,108,.55)' : 'rgba(255,255,255,.1)'}`,
          color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
        }}
      />
      {(error || hint) && (
        <div style={{ fontSize: 11, marginTop: 5, color: error ? '#F5576C' : 'var(--text-4)' }}>{error || hint}</div>
      )}
    </div>
  );
}

const BANNER = {
  empty: { bg: 'rgba(99,102,241,.08)', bd: 'rgba(99,102,241,.25)', fg: '#A5B4FC' },
  incomplete: { bg: 'rgba(99,102,241,.08)', bd: 'rgba(99,102,241,.25)', fg: '#A5B4FC' },
  no_trn: { bg: 'rgba(245,158,11,.08)', bd: 'rgba(245,158,11,.25)', fg: '#FCD34D' },
  complete: { bg: 'rgba(16,185,129,.08)', bd: 'rgba(16,185,129,.25)', fg: '#10B981' },
};

function bannerCopy(state, pending) {
  const held = pending > 0 ? ` — including ${pending} past ${pending === 1 ? 'one' : 'ones'}` : '';
  if (state === 'complete') {
    return { title: 'Your billing profile is complete.', body: 'Invoices are issued automatically after each top-up. Download them below.' };
  }
  return { title: 'Complete your billing details to unlock your invoices.', body: `Until this is filled in, invoices for your top-ups are on hold${held}. Add your details below and they'll be generated.` };
}

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  // Format in UTC: a DATE column arrives as a tz-shifted ISO string
  // (e.g. '2025-08-08T21:00:00.000Z'); local formatting would roll it a day.
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BillingTab({ isSubAccount, toast }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null); // { count }
  const pollRef = useRef(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientActions.getBillingProfile();
      const d = res?.data || null;
      setData(d);
      const p = d?.profile || {};
      setForm({
        legal_name: p.legal_name || '', address_line1: p.address_line1 || '', address_line2: p.address_line2 || '',
        city: p.city || '', country: p.country || 'United Arab Emirates', tax_id: p.tax_id || '',
        phone: p.phone || '', email: p.email || d?.default_email || '',
      });
      const inv = await clientActions.getInvoices();
      setInvoices(inv?.data || []);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    // Sub-accounts render the "managed by your main account" card and never read
    // this data, so don't request it at all. The server refuses them either way.
    if (!isSubAccount) load();
    return () => { cancelledRef.current = true; clearTimeout(pollRef.current); };
  }, [load, isSubAccount]);

  const state = (() => {
    const p = data?.profile;
    if (!p || !p.legal_name) return 'empty';
    if (!p.profile_complete) return 'incomplete';
    return 'complete';
  })();

  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: null } : e)); };

  const validate = () => {
    const e = {};
    if (!form.legal_name || form.legal_name.trim().length < 2) e.legal_name = 'Enter your registered legal name.';
    if (!form.address_line1 || !form.address_line1.trim()) e.address_line1 = 'Enter your billing address.';
    if (!form.city || !form.city.trim()) e.city = 'Enter your city.';
    if (!form.country || !form.country.trim()) e.country = 'Enter your country.';
    if (!form.email || !EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const pollInvoices = useCallback((tries = 0) => {
    clearTimeout(pollRef.current);
    if (tries >= 6) return;
    pollRef.current = setTimeout(async () => {
      try {
        const inv = await clientActions.getInvoices();
        if (cancelledRef.current) return; // unmounted mid-poll
        const list = inv?.data || [];
        setInvoices(list);
        if (list.some((i) => !i.has_pdf)) pollInvoices(tries + 1);
      } catch { /* stop polling on error */ }
    }, 5000);
  }, []);

  const onSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form, tax_id: form.tax_id ? form.tax_id.replace(/\s/g, '') : '' };
      const res = await clientActions.saveBillingProfile(payload);
      setData(res?.data || null);
      const unlocked = res?.data?.invoices_unlocked_count || 0;
      const inv = await clientActions.getInvoices();
      setInvoices(inv?.data || []);
      if (unlocked > 0) { setSuccess({ count: unlocked }); pollInvoices(0); }
      else toast?.success?.('Billing details saved');
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      const msg = err?.response?.data?.error?.message || 'Failed to save billing details';
      if (code === 'invalid_trn') setErrors((e) => ({ ...e, tax_id: msg }));
      else toast?.error?.(msg);
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async (inv) => {
    try {
      const blob = await clientActions.downloadInvoice(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast?.error?.('Could not download invoice — it may still be generating.');
    }
  };

  if (isSubAccount) {
    return (
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Billing is managed by your main account</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Tax invoices for this team's top-ups are issued to the main account. Ask your main account holder to manage billing details and invoices.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[60, 240, 160].map((h, i) => <div key={i} style={{ height: h, borderRadius: 12, background: 'rgba(255,255,255,.05)' }} />)}
      </div>
    );
  }

  const bv = BANNER[state];
  const copy = bannerCopy(state, data?.pending_invoice_count || 0);
  const statusChip = state === 'complete' ? { t: 'Complete', c: '#10B981' } : { t: 'Not started', c: 'var(--text-4)' };

  return (
    <div style={{ maxWidth: 880 }}>
      {/* Why-banner */}
      <div style={{ marginBottom: 22, padding: '14px 18px', borderRadius: 12, background: bv.bg, border: `1px solid ${bv.bd}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: bv.fg }}>{copy.title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>{copy.body}</div>
      </div>

      {/* Form */}
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Billing details</h3>
          <span className="chip" style={{ color: statusChip.c }}>{statusChip.t}</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-3)', margin: '3px 0 18px' }}>Used on every tax invoice for your top-ups.</p>

        <GroupLabel>Legal entity</GroupLabel>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Legal name" required value={form.legal_name} onChange={set('legal_name')} maxLength={255} error={errors.legal_name} placeholder="Registered company / entity name" />
          <Field label="Tax ID / VAT number (optional)" value={form.tax_id} onChange={set('tax_id')} maxLength={64} error={errors.tax_id} hint="Only if you'd like your own tax number shown on the invoice." placeholder="Optional" />
        </div>

        <Divider />
        <GroupLabel>Billing address</GroupLabel>
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Address line 1" required value={form.address_line1} onChange={set('address_line1')} maxLength={255} error={errors.address_line1} />
          <Field label="Address line 2" value={form.address_line2} onChange={set('address_line2')} maxLength={255} />
          <div className="acct-field-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="City" required value={form.city} onChange={set('city')} maxLength={128} error={errors.city} />
            <Field label="Country" required value={form.country} onChange={set('country')} maxLength={128} error={errors.country} />
          </div>
        </div>

        <Divider />
        <GroupLabel>Contact</GroupLabel>
        <div className="acct-field-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Email" required value={form.email} onChange={set('email')} maxLength={255} error={errors.email} hint="Where we send your invoices." />
          <Field label="Phone" value={form.phone} onChange={set('phone')} maxLength={64} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save billing details'}</button>
        </div>
      </div>

      {/* Invoices */}
      {(state === 'complete' || state === 'no_trn' || invoices.length > 0) && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 0' }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Invoices</h3>
            <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{invoices.length} issued</span>
          </div>
          <div style={{ padding: '10px 8px 16px' }}>
            {invoices.length === 0 ? (
              <div style={{ padding: '20px', fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>No invoices yet. They'll appear here automatically after your next top-up.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="mono" style={{ padding: '10px 12px', fontWeight: 600 }}>
                          <span style={{ textDecoration: inv.status === 'voided' ? 'line-through' : 'none' }}>{inv.invoice_number}</span>
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span className="chip" style={{ color: inv.doc_type === 'credit_note' ? '#FCD34D' : 'var(--text-3)' }}>{inv.doc_type === 'credit_note' ? 'Credit note' : 'Tax invoice'}</span>
                        </td>
                        <td style={{ padding: '10px 12px', color: 'var(--text-3)' }} title={`Issued ${fmtDate(inv.issue_date)}`}>{fmtDate(inv.supply_date)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>${Number(inv.total).toFixed(2)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          {inv.has_pdf
                            ? <button className="btn btn-ghost btn-sm" onClick={() => onDownload(inv)}>Download</button>
                            : <span style={{ fontSize: 12, color: 'var(--text-4)' }}>Preparing…</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-4)', textAlign: 'center', marginBottom: 8 }}>
        Invoices are issued by Flux Uro LLC · Sharjah, UAE.
      </div>

      {/* Unlock success modal */}
      {success && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSuccess(null)}>
          <div className="card" style={{ padding: 32, maxWidth: 380, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid #10B981', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 22 }}>✓</div>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>You're all set.</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.5 }}>{success.count} invoice{success.count === 1 ? '' : 's'} {success.count === 1 ? 'is' : 'are'} being generated and will appear below in a moment.</div>
            <button className="btn btn-primary" onClick={() => setSuccess(null)}>View invoices</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupLabel({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 12 }}>{children}</div>;
}
function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />;
}
